import { useAuthStore } from '../store/useAuthStore';
import type { ApiError } from './types';

/**
 * Forge API client — thin fetch wrapper.
 *
 * Each Lambda handler is mounted on API Gateway under `/<service>/<command>`
 * (see services/identity/handler.py et al). We don't hand-write per-endpoint
 * methods — every call goes through `apiFetch` with a tagged path.
 *
 * Auth: the Cognito ID token from the Zustand auth store is attached as a
 * `Bearer` header. The ID token (not the access token) is required because
 * downstream Lambdas read `custom:tenant_id` and `custom:role` claims via the
 * `tenant_from_event` helper in `services/shared/tenant_context.py`; Cognito
 * does not emit custom: claims on access tokens by default. Tokens are
 * proactively refreshed via `REFRESH_TOKEN_AUTH` when within
 * `REFRESH_LEEWAY_MS` of expiry; on 401 we hard sign-out and bounce to
 * /login so the user can never sit in a "logged-in but every request 401"
 * state.
 *
 * Error model: non-2xx responses raise `ApiFetchError` carrying the parsed
 * JSON envelope (Lambda handlers always return `{ error, message?, fields? }`
 * on failures). Network failures surface as `ApiFetchError` with status 0.
 */

export const API_BASE_URL =
  (import.meta.env.VITE_FORGE_API_URL as string | undefined) ?? '/api';

const COGNITO_REGION =
  (import.meta.env.VITE_COGNITO_REGION as string | undefined) ?? 'eu-central-1';
const COGNITO_CLIENT_ID =
  ((import.meta.env.VITE_COGNITO_WEB_CLIENT_ID ??
    import.meta.env.VITE_COGNITO_USER_POOL_CLIENT_ID) as string | undefined) ?? '';

/** Refresh the ID/access tokens this many ms before they expire. */
const REFRESH_LEEWAY_MS = 60_000;

export class ApiFetchError extends Error {
  readonly status: number;
  readonly body: ApiError;

  constructor(status: number, body: ApiError, cause?: unknown) {
    super(body.message ?? body.error ?? `HTTP ${status}`);
    this.name = 'ApiFetchError';
    this.status = status;
    this.body = body;
    if (cause !== undefined) {
      (this as { cause?: unknown }).cause = cause;
    }
  }
}

interface FetchOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** AbortSignal — TanStack Query supplies one per query. */
  signal?: AbortSignal;
  /** Skip auth header — used by the public `/identity/signup` endpoint. */
  anonymous?: boolean;
  /**
   * Substitutions for path template segments. The path may contain
   * `{name}` placeholders which are replaced with the matching value here,
   * URL-encoded. Example: path='/ranges/{rangeId}/snapshot', pathParams={rangeId: 7}
   */
  pathParams?: Record<string, string | number>;
  /**
   * Return the response body as text instead of parsing JSON — for the
   * gradebook CSV export, which needs the auth header and so cannot be a
   * plain link.
   */
  raw?: boolean;
}

async function readJsonSafe(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return { error: 'invalid_json', message: text.slice(0, 200) };
  }
}

/**
 * Force the browser to `/login` after a hard sign-out. Using
 * `window.location.assign` (not the router) clears every in-memory
 * TanStack-Query cache and Zustand subscription — important so the user
 * doesn't land on a page whose data was fetched with a now-revoked token.
 */
function signOutAndRedirect(): void {
  useAuthStore.getState().signOut();
  if (typeof window !== 'undefined') {
    const target = `${window.location.origin}/login`;
    if (window.location.href !== target) {
      window.location.assign(target);
    }
  }
}

let refreshPromise: Promise<string | null> | null = null;

interface RefreshResultOk {
  AuthenticationResult: {
    IdToken: string;
    AccessToken: string;
    ExpiresIn: number;
    TokenType: 'Bearer';
  };
}

/**
 * Exchange the persisted refresh token for a fresh ID/access pair via the
 * Cognito public endpoint. Returns the new ID token on success, or null if
 * the refresh failed (which forces sign-out at the call site).
 *
 * In-flight refreshes are coalesced so a burst of API calls during the
 * refresh window only triggers one network round-trip to Cognito.
 */
async function refreshIdToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;

  const state = useAuthStore.getState();
  const refreshToken = state.refreshToken;
  if (!refreshToken || !COGNITO_CLIENT_ID) return null;

  refreshPromise = (async () => {
    try {
      const url = `https://cognito-idp.${COGNITO_REGION}.amazonaws.com/`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
        },
        body: JSON.stringify({
          AuthFlow: 'REFRESH_TOKEN_AUTH',
          ClientId: COGNITO_CLIENT_ID,
          AuthParameters: { REFRESH_TOKEN: refreshToken },
        }),
      });
      if (!response.ok) return null;
      const data = (await response.json()) as RefreshResultOk;
      const auth = data.AuthenticationResult;
      if (!auth || !auth.IdToken) return null;
      const expiresIn = auth.ExpiresIn ?? 3600;
      useAuthStore.getState().refreshTokens({
        idToken: auth.IdToken,
        accessToken: auth.AccessToken,
        expiresAt: Date.now() + expiresIn * 1000,
      });
      return auth.IdToken;
    } catch {
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Return a usable ID token, refreshing first if it's missing or about to
 * expire. Returns null if no token can be produced (caller hard-signs-out).
 */
async function getFreshIdToken(): Promise<string | null> {
  const state = useAuthStore.getState();
  const { idToken, expiresAt } = state;
  if (!idToken) return null;
  if (expiresAt && Date.now() < expiresAt - REFRESH_LEEWAY_MS) {
    return idToken;
  }
  // Expired or expiring within the leeway window — try to refresh.
  return refreshIdToken();
}

export async function apiFetch<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, anonymous = false, pathParams, raw = false } = options;

  // --- dev mock short-circuit -------------------------------------------
  // api/mock.ts has always existed but was never wired in, so it was dead
  // code: with no reachable backend every screen rendered its empty state and
  // the UI could not be reviewed, demoed or designed against. It is gated
  // three ways and can never reach a deployed build:
  //   * import.meta.env.DEV is false in `vite build`,
  //   * the module is dynamically imported so it is not in the prod bundle,
  //   * localStorage['forge-mock'] = '0' turns it off in dev too.
  if (import.meta.env.DEV) {
    const { isMockOn, mockResponse } = await import('./mock');
    if (isMockOn()) {
      let mockPath = path;
      if (pathParams) {
        for (const [k, v] of Object.entries(pathParams)) {
          mockPath = mockPath.replace(`{${k}}`, encodeURIComponent(String(v)));
        }
      }
      const mocked = mockResponse(mockPath, method, body);
      if (mocked !== undefined) {
        // Mirror the real latency shape so loading states are exercised
        // rather than skipped -- a skeleton nobody ever sees is a skeleton
        // nobody has checked.
        await new Promise((r) => setTimeout(r, 120));
        return mocked as T;
      }
    }
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };

  if (!anonymous) {
    const token = await getFreshIdToken();
    if (!token) {
      signOutAndRedirect();
      throw new ApiFetchError(401, { error: 'session_expired', message: 'Sign in again to continue.' });
    }
    headers['Authorization'] = `Bearer ${token}`;
  }

  let resolvedPath = path;
  if (pathParams) {
    for (const [k, v] of Object.entries(pathParams)) {
      resolvedPath = resolvedPath.replace(`{${k}}`, encodeURIComponent(String(v)));
    }
  }
  const url = `${API_BASE_URL}${resolvedPath.startsWith('/') ? resolvedPath : `/${resolvedPath}`}`;

  let response: Response;
  try {
    const init: RequestInit = { method, headers };
    if (body !== undefined) init.body = JSON.stringify(body);
    if (signal !== undefined) init.signal = signal;
    response = await fetch(url, init);
  } catch (cause) {
    throw new ApiFetchError(0, { error: 'network_error', message: 'Failed to reach Forge API' }, cause);
  }

  if (raw && response.ok) {
    return (await response.text()) as unknown as T;
  }
  const payload = (await readJsonSafe(response)) as unknown;

  if (!response.ok) {
    if (response.status === 401 && !anonymous) {
      signOutAndRedirect();
    }
    const apiError: ApiError =
      payload && typeof payload === 'object' && 'error' in payload
        ? (payload as ApiError)
        : { error: 'http_error', message: `HTTP ${response.status}` };
    throw new ApiFetchError(response.status, apiError);
  }

  return payload as T;
}

/**
 * Encode a command path under a service. Use instead of string concatenation
 * to keep service/command names typed at call sites.
 */
export function cmd(service: string, command: string): string {
  return `/${service}/${command}`;
}
